import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";

import { Theme } from "../theme/common/theme.js";

/**
 * A media query string or a function that accepts the theme and returns a media query string.
 */
export type MediaQuery = string | ((theme: Theme) => string);

export type UseMediaQueryOptions = {
  /** The default value of the media query, if the hook is not initialized with a value. @default false */
  defaultValue?: boolean;
  /** Whether the hook will initialize reading the media query. @default true */
  initializeWithValue?: boolean;
};

/**
 * A hook that tracks the state of a media query using the [Match Media API](https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia).
 *
 * @param query - The media query to track.
 * @param options - The options for the hook.
 * @returns The current state of the media query (true if the query matches, false otherwise).
 *
 * @see https://usehooks-ts.com/react-hook/use-media-query Adapted from usehooks-ts
 *
 * @example
 * // Use any media query string
 * const isSmall = useMediaQuery(`(max-width: 450px)`);
 *
 * // Use a function to inject theme values into the query
 * const isSubNavigation = useMediaQuery((theme) => `(max-width: ${theme.subNavigationBreakpoint}px)`);
 */
export function useMediaQuery(
  query: MediaQuery,
  options?: UseMediaQueryOptions,
): boolean {
  const { defaultValue = false, initializeWithValue = true } = options ?? {};

  const theme = useTheme();
  const queryRef = useRef<string>("");
  queryRef.current = getQuery(query, theme);

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(queryRef.current);
    }
    return defaultValue;
  });

  const listener = useCallback((event: MediaQueryListEvent) => {
    setMatches(event.matches);
  }, []);

  useLayoutEffect(() => {
    if (queryRef.current == null) return;

    const matchMedia = window.matchMedia(queryRef.current);

    // Triggered at the first client-side load and if query changes
    setMatches(getMatches(queryRef.current));

    matchMedia.addEventListener("change", listener);
    return () => {
      matchMedia.removeEventListener("change", listener);
    };
  }, [query, listener]);

  return matches;
}

export function useIsSmallMediaQuery(): boolean {
  return useMediaQuery((theme) => `(max-width: ${theme.mediaQuery.SMALL}px)`);
}

function getQuery(query: MediaQuery, theme: Theme): string {
  if (typeof query === "function") return query(theme);
  return query;
}

function getMatches(query: string): boolean {
  return window.matchMedia(query).matches;
}

/** A MediaQuery or `null`, in which case the hook will return `undefined`. */
export type OptionalMediaQuery = MediaQuery | null;

/**
 * A version of `useMediaQuery` that's more useful for reusable components. It
 * accepts a `null` value for the query, in which case the hook will return
 * `undefined`, meaning a decision (whether the query matches) was not made.
 */
export function useOptionalMediaQuery(
  query: OptionalMediaQuery,
  options?: UseMediaQueryOptions,
): boolean | undefined {
  const { defaultValue = false, initializeWithValue = true } = options ?? {};

  const theme = useTheme();
  const queryRef = useRef<string | null>(null);
  queryRef.current = getOptionalQuery(query, theme);

  const [matches, setMatches] = useState<boolean | undefined>(() => {
    if (queryRef.current == null) return undefined;
    if (initializeWithValue) {
      return getOptionalMatches(queryRef.current);
    }
    return defaultValue;
  });

  const listener = useCallback((event: MediaQueryListEvent) => {
    setMatches(event.matches);
  }, []);

  useLayoutEffect(() => {
    if (queryRef.current == null) return;

    const matchMedia = window.matchMedia(queryRef.current);

    // Triggered at the first client-side load and if query changes
    setMatches(getOptionalMatches(queryRef.current));

    matchMedia.addEventListener("change", listener);
    return () => {
      matchMedia.removeEventListener("change", listener);
    };
  }, [query, listener]);

  return matches;
}

function getOptionalQuery(
  query: OptionalMediaQuery,
  theme: Theme,
): string | null {
  if (query == null) return null;
  return getQuery(query, theme);
}

function getOptionalMatches(query: string | null): boolean | undefined {
  if (query == null) return undefined;
  return getMatches(query);
}
